import { useEffect, useState, type ReactElement } from 'react';
import { AuthProvider, useAuth } from './auth';
import { LOGIN_PATH, RouterProvider, gate, match, useNavigate, useRoute } from './router';
import { Logo } from './components/Logo';
import {
  IconActors,
  IconClose,
  IconDashboard,
  IconIdentity,
  IconKey,
  IconMenu,
  IconMonitor,
  IconPool,
  IconProxy,
  IconRuns,
  IconSettings,
} from './components/icons';
import { Actors } from './pages/Actors';
import { ApiKeys } from './pages/ApiKeys';
import { Dashboard } from './pages/Dashboard';
import { Landing } from './pages/Landing';
import { InstagramAccounts } from './pages/InstagramAccounts';
import { Login } from './pages/Login';
import { Operations } from './pages/Operations';
import { Pools } from './pages/Pools';
import { Proxies } from './pages/Proxies';
import { RunDetail } from './pages/RunDetail';
import { Runs } from './pages/Runs';
import { Settings } from './pages/Settings';

interface NavItem {
  path: string;
  label: string;
  title: string;
  subtitle: string;
  icon: () => ReactElement;
}

const NAV: Array<{ group: string; items: NavItem[] }> = [
  {
    group: 'Genel',
    items: [
      {
        path: '/',
        label: 'Panel',
        title: 'Panel',
        subtitle: 'Platformun ve kimlik havuzunun anlık durumu',
        icon: IconDashboard,
      },
      {
        path: '/runs',
        label: 'Çalışmalar',
        title: 'Çalışmalar',
        subtitle: 'Actor çalışmaları ve sonuçları',
        icon: IconRuns,
      },
      {
        path: '/actors',
        label: "Actor'lar",
        title: "Actor'lar",
        subtitle: 'Kayıtlı actor kataloğu',
        icon: IconActors,
      },
      {
        path: '/operations',
        label: 'İzleme',
        title: 'İzleme',
        subtitle: 'Tarama, görsel analizi, hatalar ve harcama',
        icon: IconMonitor,
      },
    ],
  },
  {
    group: 'Kaynaklar',
    items: [
      {
        path: '/instagram-accounts',
        label: 'Instagram hesapları',
        title: 'Instagram hesapları',
        subtitle: 'Scraping kimlikleri, sağlıkları ve bütçeleri',
        icon: IconIdentity,
      },
      {
        path: '/proxies',
        label: "Proxy'ler",
        title: "Proxy'ler",
        subtitle: 'Çıkış adresleri ve erişilebilirlikleri',
        icon: IconProxy,
      },
      {
        path: '/pools',
        label: 'Havuzlar',
        title: 'Kaynak havuzları',
        subtitle: 'Kimlik ve proxy gruplamaları',
        icon: IconPool,
      },
    ],
  },
  {
    group: 'Yönetim',
    items: [
      {
        path: '/api-keys',
        label: 'API anahtarları',
        title: 'API anahtarları',
        subtitle: 'Makine erişimi — yalnızca actor, çalışma ve veri kümesi',
        icon: IconKey,
      },
      {
        path: '/settings',
        label: 'Ayarlar',
        title: 'Ayarlar',
        subtitle: 'Hesap, çalışma alanı ve platform durumu',
        icon: IconSettings,
      },
    ],
  },
];

const ITEMS = NAV.flatMap((section) => section.items);

function Screen() {
  const { path } = useRoute();

  const runDetail = match('/runs/:runId', path);
  if (runDetail) return <RunDetail runId={runDetail.runId!} />;

  switch (path) {
    case '/':
      return <Dashboard />;
    case '/runs':
      return <Runs />;
    case '/actors':
      return <Actors />;
    case '/operations':
      return <Operations />;
    case '/instagram-accounts':
      return <InstagramAccounts />;
    case '/proxies':
      return <Proxies />;
    case '/pools':
      return <Pools />;
    case '/api-keys':
      return <ApiKeys />;
    case '/settings':
      return <Settings />;
    default:
      return <p className="empty">Böyle bir sayfa yok.</p>;
  }
}

/** Başlık, yolun kendisinden türetilir; her sayfanın ayrıca başlık basması gerekmez. */
function heading(path: string): { title: string; subtitle: string } {
  if (match('/runs/:runId', path)) {
    return { title: 'Çalışma ayrıntısı', subtitle: 'Girdi, sonuç ve kullanılan kaynaklar' };
  }
  const item = ITEMS.find((entry) => entry.path === path);
  return item ? { title: item.title, subtitle: item.subtitle } : { title: 'Rab-bit', subtitle: '' };
}

function Shell() {
  const { session, logout } = useAuth();
  const { path: rawPath } = useRoute();
  const navigate = useNavigate();

  const screen = gate(rawPath, session !== null);
  const path = screen.kind === 'app' ? screen.path : rawPath;

  // Giriş başarılı olduğunda adres `#/giris`te kalıyor. `gate` doğru sayfayı zaten
  // çiziyor; bu da adresi düzeltiyor, yoksa yenilemede yine forma dönerdi.
  useEffect(() => {
    if (session && rawPath === LOGIN_PATH) navigate('/');
  }, [session, rawPath, navigate]);

  /**
   * Dar ekranda kenar çubuğu bir çekmeceye dönüşüyor. Ölçüldü: 375 px'lik bir telefonda
   * satır halinde sarılan menü 190 px yer kaplıyordu, yani ekranın dörtte biri her
   * sayfada içerikten önce menüye gidiyordu. Çekmece kapalıyken o alan içeriğe kalıyor.
   */
  const [menuOpen, setMenuOpen] = useState(false);

  // Bir sayfaya geçildiğinde çekmece kendiliğinden kapanmalı; kullanıcının iki kez
  // dokunması gerekmesin.
  useEffect(() => setMenuOpen(false), [path]);

  // Çekmece açıkken arkadaki sayfanın kayması, kapanınca kullanıcıyı başka bir yerde
  // bırakıyordu.
  useEffect(() => {
    document.body.classList.toggle('menu-open', menuOpen);
    return () => document.body.classList.remove('menu-open');
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  if (screen.kind === 'landing') return <Landing />;
  // `!session` mantıken gereksiz — `gate` oturumsuzken asla 'app' döndürmüyor. Derleyici
  // bunu göremiyor ve buradaki daraltma onun için; kaldırmak `session!` yazmak olurdu.
  if (screen.kind === 'login' || !session) return <Login />;

  const workspace = session.workspaces.find((w) => w.id === session.workspaceId);
  const { title, subtitle } = heading(path);
  const initials = (session.user.displayName ?? session.user.email).slice(0, 2);

  return (
    <div className={menuOpen ? 'app menu-open' : 'app'}>
      <button
        type="button"
        className="scrim"
        aria-label="Menüyü kapat"
        tabIndex={menuOpen ? 0 : -1}
        onClick={() => setMenuOpen(false)}
      />

      <nav className="sidebar">
        <div className="sidebar-top">
          <Logo id="logo-nav" subtitle={workspace?.name ?? 'çalışma alanı'} />
          <button
            type="button"
            className="iconbtn drawer-close"
            aria-label="Menüyü kapat"
            onClick={() => setMenuOpen(false)}
          >
            <IconClose />
          </button>
        </div>

        {NAV.map((section) => (
          <div className="nav-group" key={section.group}>
            <div className="nav-title">{section.group}</div>
            <ul>
              {section.items.map((item) => {
                const Icon = item.icon;
                const active =
                  path === item.path || (item.path === '/runs' && path.startsWith('/runs/'));
                return (
                  <li key={item.path}>
                    <button
                      type="button"
                      className={active ? 'nav active' : 'nav'}
                      onClick={() => navigate(item.path)}
                    >
                      <Icon />
                      {item.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        <div className="sidebar-foot">
          <button type="button" className="user-chip" onClick={() => navigate('/settings')}>
            <span className="avatar">{initials}</span>
            <span className="user-mail">{session.user.email}</span>
          </button>
        </div>
      </nav>

      <div className="content">
        <header className="topbar">
          <button
            type="button"
            className="iconbtn menu-btn"
            aria-label="Menü"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            <IconMenu />
          </button>
          <div className="topbar-title">
            <h1>{title}</h1>
            {subtitle && <div className="topbar-sub">{subtitle}</div>}
          </div>
          <div className="topbar-actions">
            <button type="button" className="ghost" onClick={logout}>
              Çıkış
            </button>
          </div>
        </header>
        <main className="page">
          <Screen />
        </main>
      </div>
    </div>
  );
}

export function App() {
  return (
    <AuthProvider>
      <RouterProvider>
        <Shell />
      </RouterProvider>
    </AuthProvider>
  );
}
